import React, { useState } from 'react';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Container, 
  Grid, 
  Paper, 
  Avatar,
  Badge,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  Fab,
  Tooltip,
  ListItemButton
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  Store as StoreIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Add as AddIcon,
  Public as PublicIcon,
  LibraryBooks as LibraryBooksIcon,
  Category as CategoryIcon,
  Attribution as AttributionIcon,
  Inventory2 as Inventory2Icon,
  Language as LanguageIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  Star as StarIcon,
  People as PeopleIcon,
  LocalShipping as LocalShippingIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  SupportAgent as SupportAgentIcon,
  Menu as MenuIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';

// Import dashboard pages
import StoreOwnerDashboardHome from './Dashboard';
import StoreOwnerProducts from './Products';
import AddProduct from './AddProduct';
import MyProducts from './MyProducts';
import MyStore from './MyStore';
import StoreOwnerOrders from './Orders';
import StoreOwnerAnalytics from './Analytics';
import StoreOwnerSettings from './Settings';

const AppBarStyled = styled(AppBar)(({ theme }) => ({
  background: 'linear-gradient(45deg, #4e279d 30%, #1275cb 90%)',
  position: 'fixed',
  zIndex: theme.zIndex.drawer + 1,
}));

const StoreOwnerDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [productsMenuAnchor, setProductsMenuAnchor] = useState(null);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [isHoveringProducts, setIsHoveringProducts] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [language, setLanguage] = useState('bn'); // 'bn' for Bangla, 'en' for English
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageMenuOpen = (event) => {
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    handleLanguageMenuClose();
  };

  const handleProductsMenuOpen = (event) => {
    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setIsHoveringProducts(true);
    setProductsMenuAnchor(event.currentTarget);
  };

  const handleProductsMenuClose = () => {
    setIsHoveringProducts(false);
    setProductsMenuAnchor(null);
  };

  const handleProductsMenuEnter = (event) => {
    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    
    // Only open if not already hovering (prevents repeated opening)
    if (!isHoveringProducts) {
      setIsHoveringProducts(true);
      setProductsMenuAnchor(event.currentTarget);
    }
  };

  const handleProductsMenuLeave = () => {
    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    
    // Set a longer timeout to close the menu after leaving
    const timeout = setTimeout(() => {
      if (!isHoveringProducts) {
        setProductsMenuAnchor(null);
      }
    }, 300);
    setHoverTimeout(timeout);
  };

  const handleDropdownEnter = () => {
    // Clear the timeout when hovering over the dropdown
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setIsHoveringProducts(true);
  };

  const handleDropdownLeave = () => {
    setIsHoveringProducts(false);
    // Set a timeout to close the menu when leaving the dropdown
    const timeout = setTimeout(() => {
      setProductsMenuAnchor(null);
    }, 200);
    setHoverTimeout(timeout);
  };

  const handleLogout = () => {
    handleMenuClose();
    // In a real app, this would handle logout logic
    navigate('/login');
  };

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  const handleMobileNavigation = (path) => {
    navigate(path);
    setMobileDrawerOpen(false);
  };

  const handleMobileTabChange = (newValue) => {
    setCurrentTab(newValue);
    setMobileDrawerOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    // Don't navigate if it's the Products tab (index 2) - hover will handle dropdown
    if (newValue === 2) {
      return;
    }
    setCurrentTab(newValue);
    navigate(menuItems[newValue].path);
  };

  const handleProductsNavigation = (path) => {
    navigate(path);
    handleProductsMenuClose();
    // Set current tab to Products when navigating to any products page
    setCurrentTab(2);
  };

  // Translations
  const translations = {
    bn: {
      title: 'দোকান মালিক ড্যাশবোর্ড',
      dashboard: 'ড্যাশবোর্ড',
      myStore: 'আমার দোকান',
      products: 'পণ্যসমূহ',
      orders: 'অর্ডারসমূহ',
      analytics: 'বিশ্লেষণ',
      settings: 'সেটিংস',
      profile: 'প্রোফাইল',
      logout: 'লগআউট',
      productsLibraries: 'পণ্য লাইব্রেরি',
      addProducts: 'পণ্য যোগ করুন',
      addProductCategory: 'পণ্যের ক্যাটেগরি যোগ করুন',
      addAttributes: 'বৈশিষ্ট্য যোগ করুন',
      myProducts: 'আমার পণ্যসমূহ',
      language: 'ভাষা',
      bangla: 'বাংলা',
      english: 'English'
    },
    en: {
      title: 'Store Owner Dashboard',
      dashboard: 'Dashboard',
      myStore: 'My Store',
      products: 'Products',
      orders: 'Orders',
      analytics: 'Analytics',
      settings: 'Settings',
      profile: 'Profile',
      logout: 'Logout',
      productsLibraries: 'Products Libraries',
      addProducts: 'Add Products',
      addProductCategory: 'Add Product Category',
      addAttributes: 'Add Attributes',
      myProducts: 'My Products',
      language: 'Language',
      bangla: 'বাংলা',
      english: 'English'
    }
  };

  const t = translations[language];

  const menuItems = [
    { text: t.dashboard, icon: <DashboardIcon />, path: '/store-owner/dashboard' },
    { text: t.myStore, icon: <StoreIcon />, path: '/store-owner/dashboard/store' },
    { text: t.products, icon: <InventoryIcon />, path: '/store-owner/dashboard/products', hasDropdown: true },
    { text: t.orders, icon: <ShoppingCartIcon />, path: '/store-owner/dashboard/orders' },
    { text: t.analytics, icon: <BarChartIcon />, path: '/store-owner/dashboard/analytics' },
    { text: t.settings, icon: <SettingsIcon />, path: '/store-owner/dashboard/settings' },
  ];

  const productsMenuItems = [
    { text: t.productsLibraries, path: '/store-owner/dashboard/products', icon: <LibraryBooksIcon /> },
    { text: t.addProducts, path: '/store-owner/dashboard/products/add', icon: <AddIcon /> },
    { text: t.myProducts, path: '/store-owner/dashboard/products/my-products', icon: <Inventory2Icon /> },
  ];

  // Set current tab based on current path
  React.useEffect(() => {
    const currentIndex = menuItems.findIndex(item => {
      if (item.hasDropdown && location.pathname.includes('/products')) {
        return true;
      }
      return item.path === location.pathname;
    });
    if (currentIndex !== -1) {
      setCurrentTab(currentIndex);
    }
  }, [location.pathname]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <AppBarStyled>
        <Toolbar>
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleMobileDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            sx={{ flexGrow: 1 }}
          >
            {t.title}
          </Typography>
          
          {/* Store Status Buttons */}
          <Box sx={{ 
            display: { xs: 'none', sm: 'flex' }, 
            gap: 1, 
            mr: 2 
          }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<VisibilityIcon />}
              component="a"
              href="/"
              target="_blank"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)'
                }
              }}
            >
              দোকান দেখুন
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              sx={{
                borderColor: 'rgba(255,255,255,0.5)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              সম্পাদনা
            </Button>
          </Box>
          
          {/* Language Switcher */}
          <IconButton 
            color="inherit" 
            onClick={handleLanguageMenuOpen}
            sx={{ mr: 1 }}
          >
            <LanguageIcon />
          </IconButton>
          
          {/* View Site Icon */}
          <IconButton 
            color="inherit" 
            component="a" 
            href="/" 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ mr: 1 }}
          >
            <PublicIcon />
          </IconButton>
          
          <IconButton color="inherit">
            <Badge badgeContent={2} color="secondary">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <IconButton
            color="inherit"
            onClick={handleProfileMenuOpen}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>S</Avatar>
          </IconButton>
          
          {/* Profile Menu */}
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            disableScrollLock={true}
          >
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              {t.profile}
            </MenuItem>
            <MenuItem onClick={handleMenuClose}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              {t.settings}
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {t.logout}
            </MenuItem>
          </Menu>
          
          {/* Language Menu */}
          <Menu
            anchorEl={languageMenuAnchor}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(languageMenuAnchor)}
            onClose={handleLanguageMenuClose}
            disableScrollLock={true}
          >
            <MenuItem 
              onClick={() => handleLanguageChange('bn')}
              selected={language === 'bn'}
            >
              {t.bangla}
            </MenuItem>
            <MenuItem 
              onClick={() => handleLanguageChange('en')}
              selected={language === 'en'}
            >
              {t.english}
            </MenuItem>
          </Menu>
          
          {/* Products Dropdown Menu */}
          <Menu
            anchorEl={productsMenuAnchor}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            open={Boolean(productsMenuAnchor)}
            onClose={handleProductsMenuClose}
            disableScrollLock={true}
            MenuListProps={{
              onMouseEnter: handleDropdownEnter,
              onMouseLeave: handleDropdownLeave,
            }}
            sx={{
              mt: 1,
              '& .MuiPaper-root': {
                backgroundColor: 'white',
                minWidth: 200,
              }
            }}
          >
            {productsMenuItems.map((item) => (
              <MenuItem 
                key={item.text}
                onClick={() => handleProductsNavigation(item.path)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </MenuItem>
            ))}
          </Menu>
        </Toolbar>
        
        {/* Navigation Tabs - Hidden on Mobile */}
        {!isMobile && (
          <Box sx={{ 
            borderBottom: 1, 
            borderColor: 'rgba(255,255,255,0.2)',
            background: 'linear-gradient(45deg, #3d1789 30%, #0a6abd 90%)'
          }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange}
              textColor="inherit"
              TabIndicatorProps={{
                style: { backgroundColor: 'white' }
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  color: 'rgba(255,255,255,0.8)',
                  '&.Mui-selected': {
                    color: 'white'
                  }
                }
              }}
            >
              {menuItems.map((item, index) => (
                <Tab 
                  key={item.text}
                  label={item.text} 
                  icon={item.icon}
                  iconPosition="start"
                  sx={{ minWidth: 120 }}
                  onMouseEnter={item.hasDropdown ? handleProductsMenuEnter : undefined}
                  onMouseLeave={item.hasDropdown ? handleProductsMenuLeave : undefined}
                />
              ))}
            </Tabs>
          </Box>
        )}
              </AppBarStyled>

        {/* Mobile Navigation Drawer */}
        <SwipeableDrawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={handleMobileDrawerClose}
          onOpen={() => setMobileDrawerOpen(true)}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
              backgroundColor: 'white',
            },
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {t.title}
              </Typography>
              <IconButton onClick={handleMobileDrawerClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          
          <List component="nav" sx={{ p: 1 }}>
            {menuItems.map((item, index) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  onClick={() => {
                    if (item.hasDropdown) {
                      // For products menu, open the first item
                      handleMobileNavigation('/store-owner/dashboard/products');
                    } else {
                      handleMobileNavigation(item.path);
                    }
                  }}
                  selected={location.pathname === item.path || (item.hasDropdown && location.pathname.includes('/products'))}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'primary.light',
                      color: 'primary.contrastText',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                  {item.hasDropdown && (
                    <Badge badgeContent={productsMenuItems.length} color="primary" />
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          
          <Divider sx={{ my: 2 }} />
          
          {/* Products Submenu in Mobile Drawer */}
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
              {t.products}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {productsMenuItems.map((item) => (
                <Button
                  key={item.text}
                  variant="outlined"
                  size="small"
                  startIcon={item.icon}
                  fullWidth
                  component={Link}
                  to={item.path}
                  onClick={handleMobileDrawerClose}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  {item.text}
                </Button>
              ))}
            </Box>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          {/* Quick Actions in Mobile Drawer */}
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
              দ্রুত কার্যক্রম
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                fullWidth
                component={Link}
                to="/store-owner/dashboard/products/add"
                onClick={handleMobileDrawerClose}
                sx={{ justifyContent: 'flex-start' }}
              >
                পণ্য যোগ করুন
              </Button>
              
              <Button
                variant="outlined"
                size="small"
                startIcon={<ShoppingCartIcon />}
                fullWidth
                component={Link}
                to="/store-owner/dashboard/orders"
                onClick={handleMobileDrawerClose}
                sx={{ justifyContent: 'flex-start' }}
              >
                অর্ডার দেখুন
              </Button>
            </Box>
          </Box>
        </SwipeableDrawer>

        {/* Mobile Floating Action Button */}
        {isMobile && (
          <Fab
            color="primary"
            aria-label="menu"
            onClick={handleMobileDrawerToggle}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              zIndex: 1000,
              display: { xs: 'flex', md: 'none' },
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)'
            }}
          >
            <MenuIcon />
          </Fab>
        )}
        
        {/* Main Content Area with Sidebar */}
        <Box sx={{ display: 'flex', pt: { xs: 8, md: 16 } }}>
        {/* Left Sidebar - Hidden on Mobile */}
        {!isMobile && (
          <Box
            sx={{
              width: 280,
              backgroundColor: 'white',
              borderRight: '1px solid #e0e0e0',
              minHeight: 'calc(100vh - 128px)',
              p: 2,
              position: 'sticky',
              top: 128,
            }}
          >
          {/* Quick Actions */}
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            দ্রুত কার্যক্রম
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              fullWidth
              component={Link}
              to="/store-owner/dashboard/products/add"
              sx={{ justifyContent: 'flex-start' }}
            >
              পণ্য যোগ করুন
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<ShoppingCartIcon />}
              fullWidth
              component={Link}
              to="/store-owner/dashboard/orders"
              sx={{ justifyContent: 'flex-start' }}
            >
              অর্ডার দেখুন
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<BarChartIcon />}
              fullWidth
              component={Link}
              to="/store-owner/dashboard/analytics"
              sx={{ justifyContent: 'flex-start' }}
            >
              বিশ্লেষণ দেখুন
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<StoreIcon />}
              fullWidth
              href="/"
              target="_blank"
              sx={{ justifyContent: 'flex-start' }}
            >
              দোকান দেখুন
            </Button>
          </Box>
          
          {/* Quick Stats */}
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            আজকের পরিসংখ্যান
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{ p: 2, bgcolor: '#f8f9fa' }}>
              <Typography variant="body2" color="text.secondary">
                আজকের অর্ডার
              </Typography>
              <Typography variant="h4" color="primary">
                ১২
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 2, bgcolor: '#f8f9fa' }}>
              <Typography variant="body2" color="text.secondary">
                আজকের বিক্রয়
              </Typography>
              <Typography variant="h4" color="success.main">
                ৳২,৮৫০
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 2, bgcolor: '#f8f9fa' }}>
              <Typography variant="body2" color="text.secondary">
                নতুন গ্রাহক
              </Typography>
              <Typography variant="h4" color="info.main">
                ৫
              </Typography>
            </Paper>
          </Box>
          
          {/* Recent Notifications */}
          <Typography 
            variant="h6" 
            sx={{ 
              mt: 3, 
              mb: 2, 
              fontWeight: 600,
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            সাম্প্রতিক বিজ্ঞপ্তি
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Paper sx={{ p: 2, bgcolor: '#fff3e0' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                নতুন অর্ডার
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ২ মিনিট আগে
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 2, bgcolor: '#f3e5f5' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                কম স্টক সতর্কতা
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ১৫ মিনিট আগে
              </Typography>
            </Paper>
          </Box>
        </Box>
        )}
        
        {/* Main Content */}
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[100]
                : theme.palette.grey[900],
            flexGrow: 1,
            minHeight: 'calc(100vh - 128px)',
            p: { xs: 2, md: 3 },
            width: '100%',
          }}
        >
          <Routes>
            <Route path="/" element={<StoreOwnerDashboardHome />} />
            <Route path="/products" element={<StoreOwnerProducts />} />
            <Route path="/products/add" element={<AddProduct />} />
            <Route path="/products/categories/add" element={<div>Add Product Category Page</div>} />
            <Route path="/products/attributes/add" element={<div>Add Attributes Page</div>} />
            <Route path="/products/my-products" element={<MyProducts />} />
            <Route path="/orders" element={<StoreOwnerOrders />} />
            <Route path="/analytics" element={<StoreOwnerAnalytics />} />
            <Route path="/settings" element={<StoreOwnerSettings />} />
            <Route path="/store" element={<MyStore />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};

// Default dashboard home component
const Dashboard = () => {
  // Mock data with Bangla content
  const recentOrders = [
    { id: 1, customer: 'জন ডো', date: '২৫ মার্চ, ২০২৫', status: 'ডেলিভারি সম্পন্ন', amount: 250, placedBySupport: false },
    { id: 2, customer: 'জেইন স্মিথ', date: '২৫ মার্চ, ২০২৫', status: 'প্রসেসিং', amount: 180, placedBySupport: true, supportAgent: 'সাকিব হাসান' },
    { id: 3, customer: 'বব জনসন', date: '২৪ মার্চ, ২০২৫', status: 'অপেক্ষমান', amount: 320, placedBySupport: false },
    { id: 4, customer: 'এলিস ব্রাউন', date: '২৪ মার্চ, ২০২৫', status: 'শিপ করা হয়েছে', amount: 150, placedBySupport: true, supportAgent: 'নাসিরা বেগম' },
  ];

  const topProducts = [
    { id: 1, name: 'টাজা টমেটো', sold: 120, revenue: 14400, stock: 45 },
    { id: 2, name: 'অর্গানিক আলু', sold: 85, revenue: 6800, stock: 32 },
    { id: 3, name: 'সবুজ পালং শাক', sold: 75, revenue: 4500, stock: 28 },
    { id: 4, name: 'লাল পেঁয়াজ', sold: 65, revenue: 5850, stock: 40 },
  ];

  return (
    <Box>
      {/* First Div - Statistics Cards */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2}>
          {/* Enhanced Statistics Cards */}
          <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2, width: 48, height: 48 }}>
                  <InventoryIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    মোট পণ্য
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>২৪</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2, width: 48, height: 48 }}>
                  <ShoppingCartIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    মোট অর্ডার
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>১৫৬</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2, width: 48, height: 48 }}>
                  <AttachMoneyIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    মাসিক আয়
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>৳৪৫,২৮০</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2, width: 48, height: 48 }}>
                  <StarIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    দোকানের রেটিং
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>৪.৮/৫</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', color: '#333' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.7)', mr: 2, width: 48, height: 48 }}>
                  <PeopleIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    মোট গ্রাহক
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>৩৪২</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card sx={{ background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', color: '#333' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.7)', mr: 2, width: 48, height: 48 }}>
                  <LocalShippingIcon />
                </Avatar>
                <Box>
                  <Typography color="inherit" variant="body2" gutterBottom>
                    পেন্ডিং ডেলিভারি
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>৮</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Second Div - Main Content Sections */}
      <Box>
        <Grid container spacing={2}>
          {/* Top Products */}
          <Grid item xs={12} md={6} lg={4}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  সর্বাধিক বিক্রিত পণ্য
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small"
                  startIcon={<AddIcon />}
                  component={Link}
                  to="/store-owner/dashboard/products/add"
                >
                  নতুন পণ্য
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>পণ্যের নাম</TableCell>
                      <TableCell align="right">বিক্রিত</TableCell>
                      <TableCell align="right">আয়</TableCell>
                      <TableCell align="right">স্টক</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topProducts.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>{product.name}</TableCell>
                        <TableCell align="right">{product.sold}</TableCell>
                        <TableCell align="right">৳{product.revenue}</TableCell>
                        <TableCell align="right">{product.stock}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Recent Orders */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  সাম্প্রতিক অর্ডার
                </Typography>
                <Button variant="outlined" size="small">
                  সব দেখুন
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>অর্ডার আইডি</TableCell>
                      <TableCell>গ্রাহক</TableCell>
                      <TableCell>অবস্থা</TableCell>
                      <TableCell align="right">পরিমাণ</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentOrders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>#{order.id}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {order.customer}
                            </Typography>
                            {order.placedBySupport && (
                              <Box sx={{ mt: 0.5 }}>
                                <Chip 
                                  label="কাস্টমার সাপোর্ট"
                                  size="small"
                                  icon={<SupportAgentIcon />}
                                  sx={{ 
                                    bgcolor: '#e3f2fd',
                                    color: '#1976d2',
                                    fontSize: '0.6rem',
                                    height: '18px',
                                    '& .MuiChip-icon': {
                                      fontSize: '10px'
                                    }
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={order.status} 
                            color={
                              order.status === 'ডেলিভারি সম্পন্ন' ? 'success' :
                              order.status === 'শিপ করা হয়েছে' ? 'info' :
                              order.status === 'প্রসেসিং' ? 'primary' : 'warning'
                            } 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell align="right">৳{order.amount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Low Stock Alert */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                কম স্টক সতর্কতা
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Paper sx={{ p: 2, bgcolor: '#fff3e0', border: '1px solid #ffcc02' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        সবুজ মরিচ
                      </Typography>
                      <Typography variant="body2" color="warning.main">
                        মাত্র ৫ কেজি বাকি
                      </Typography>
                    </Box>
                    <Button variant="contained" size="small" color="warning">
                      স্টক করুন
                    </Button>
                  </Box>
                </Paper>
                
                <Paper sx={{ p: 2, bgcolor: '#ffebee', border: '1px solid #f44336' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        টাজা গাজর
                      </Typography>
                      <Typography variant="body2" color="error.main">
                        মাত্র ৩ কেজি বাকি
                      </Typography>
                    </Box>
                    <Button variant="contained" size="small" color="error">
                      স্টক করুন
                    </Button>
                  </Box>
                </Paper>
                
                <Paper sx={{ p: 2, bgcolor: '#e8f5e8', border: '1px solid #4caf50' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        অর্গানিক লেটুস
                      </Typography>
                      <Typography variant="body2" color="success.main">
                        মাত্র ২ বান্ডিল বাকি
                      </Typography>
                    </Box>
                    <Button variant="contained" size="small" color="success">
                      স্টক করুন
                    </Button>
                  </Box>
                </Paper>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export { Dashboard as default, StoreOwnerDashboard };
